export interface PriceQuote {
  currency: string;
  amount: number;
  regional: boolean;
}

export interface PricingQuote {
  ppp: boolean;
  monthly: PriceQuote | null;
  yearly: PriceQuote | null;
  dayPass: PriceQuote;
  // Null means no authenticated customer history was available.
  trialEligible: boolean | null;
}

const ZERO_DECIMAL = new Set(['jpy', 'krw', 'vnd']);

export function formatQuoteAmount(price: PriceQuote, lang: string): string {
  return new Intl.NumberFormat(lang, {
    style: 'currency', currency: price.currency.toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: ZERO_DECIMAL.has(price.currency) ? 0 : 2,
  }).format(ZERO_DECIMAL.has(price.currency) ? price.amount : price.amount / 100);
}

const PERIODS: Record<string, [string, string]> = {
  ja: ['/月', '/年'], zh: ['/月', '/年'], en: ['/mo', '/yr'],
  ko: ['/월', '/년'], es: ['/mes', '/año'], fr: ['/mois', '/an'],
  de: ['/Monat', '/Jahr'], pt: ['/mês', '/ano'],
};

export function formatRecurringQuote(price: PriceQuote | null | undefined, lang: string, interval: 'month' | 'year'): string {
  if (!price) return lang === 'ja' ? '決済画面で料金を確認' : lang === 'zh' ? '在结账页面确认价格' : 'Price confirmed at checkout';
  return formatQuoteAmount(price, lang) + (PERIODS[lang] ?? PERIODS.en)[interval === 'month' ? 0 : 1];
}

export function annualSavings(monthly?: PriceQuote | null, yearly?: PriceQuote | null): number | null {
  if (!monthly || !yearly || monthly.currency !== yearly.currency || monthly.amount <= 0) return null;
  // Round down so the advertised saving never exceeds the real saving.
  return Math.max(0, Math.floor((1 - yearly.amount / (monthly.amount * 12)) * 100 + 1e-8));
}

const CHECKOUT_COPY: Record<string, { restart: string; paid: string; trial: string; conditions: string }> = {
  ja: { restart: 'Proを再開する', paid: '無料トライアルは使用済みです。決済完了時から課金されます。いつでも解約できます。', trial: '7日間無料で試す', conditions: '無料トライアルは対象のお客様に7日間。カード登録が必要です。料金と適用条件は決済画面で確認でき、期間内に解約すると請求されません。' },
  en: { restart: 'Restart Pro', paid: 'Your free trial has been used. Billing starts when you complete checkout. Cancel anytime.', trial: 'Try free for 7 days', conditions: 'Eligible customers get a 7-day free trial. Card required. Review the price and eligibility at checkout; cancel during the trial to avoid a charge.' },
  zh: { restart: '恢复 Pro', paid: '免费试用已使用。完成结账后立即开始计费，可随时取消。', trial: '免费试用7天', conditions: '符合条件的用户可免费试用7天，需要绑定银行卡。请在结账时确认价格与试用资格；试用期内取消不会扣款。' },
  ko: { restart: 'Pro 다시 시작', paid: '무료 체험을 이미 사용했습니다. 결제 완료 시 청구가 시작됩니다. 언제든 해지할 수 있습니다.', trial: '7일 무료 체험', conditions: '대상 고객은 7일 무료 체험이 가능합니다. 카드 등록이 필요합니다. 결제 화면에서 가격과 자격을 확인하고 체험 기간 내 해지하면 청구되지 않습니다.' },
  es: { restart: 'Reactivar Pro', paid: 'Ya has usado la prueba gratuita. Se te cobrará al completar el pago. Puedes cancelar cuando quieras.', trial: 'Prueba gratis 7 días', conditions: 'Los clientes elegibles tienen 7 días de prueba gratis. Se requiere tarjeta. Revisa el precio y las condiciones al pagar; cancela durante la prueba para evitar cargos.' },
  fr: { restart: 'Réactiver Pro', paid: 'Vous avez déjà utilisé l’essai gratuit. La facturation commence à la validation du paiement. Résiliez à tout moment.', trial: 'Essai gratuit de 7 jours', conditions: 'Les clients éligibles bénéficient de 7 jours gratuits. Carte requise. Vérifiez le prix et les conditions au paiement ; résiliez pendant l’essai pour éviter tout débit.' },
  de: { restart: 'Pro reaktivieren', paid: 'Die kostenlose Testphase wurde bereits genutzt. Die Abrechnung beginnt beim Abschluss des Checkouts. Jederzeit kündbar.', trial: '7 Tage kostenlos testen', conditions: 'Berechtigte Kunden erhalten 7 kostenlose Tage. Karte erforderlich. Preis und Bedingungen werden im Checkout angezeigt; bei Kündigung während der Testphase fallen keine Kosten an.' },
  pt: { restart: 'Reativar Pro', paid: 'Você já usou o teste grátis. A cobrança começa ao concluir o pagamento. Cancele quando quiser.', trial: 'Teste grátis por 7 dias', conditions: 'Clientes elegíveis têm 7 dias grátis. É necessário cadastrar um cartão. Confira o preço e as condições no pagamento; cancele durante o teste para evitar cobrança.' },
};

export function subscriptionCheckoutCopy(lang: string, eligible: boolean | null | undefined) {
  const copy = CHECKOUT_COPY[lang] ?? CHECKOUT_COPY.en;
  return { cta: eligible === false ? copy.restart : copy.trial, note: eligible === false ? copy.paid : copy.conditions };
}
